import { useTheme } from '../ThemeProvider';

import React, { useId } from 'react';
import Svg, { LinearGradient, Path, Stop } from 'react-native-svg';

export const AI_LOGO_SIZES = ['sm', 'md', 'lg'] as const;
type AILogoSize = (typeof AI_LOGO_SIZES)[number] | number;
const SVG_SIZES = {
    sm: 18,
    md: 20,
    lg: 24,
};
type AILogoStyle = 'stroke' | 'filled' | 'color';

const getSvgSize = (size: AILogoSize): number => {
    if (typeof size === 'number') {
        return size;
    }
    return SVG_SIZES[size];
};

interface AILogoProps {
    /**
     * Size of the AI logo. Can be one of the preset sizes: `sm` (18), `md` (20), `lg` (24),
     * or a custom numeric value for specific dimensions.
     */
    size?: AILogoSize;

    style?: AILogoStyle;

    /**
     * Provides a human-readable title for the element that contains it.
     * https://www.w3.org/TR/SVG-access/#Equivalent
     */
    title?: string;

    /**
     * Overrides the text that's read by the screen reader when the user interacts with the element.
     * By default, the label is constructed by traversing all the children and accumulating all the
     * Text nodes separated by space.
     */
    accessibilityLabel?: string;
}

export function AILogo({ title, size = 'md', style = 'color', accessibilityLabel }: AILogoProps) {
    switch (style) {
        case 'color':
            return (
                <AILogoColor title={title} size={size} accessibilityLabel={accessibilityLabel} />
            );
        case 'stroke':
            return (
                <AILogoStroke title={title} size={size} accessibilityLabel={accessibilityLabel} />
            );
        case 'filled':
            return (
                <AILogoFilled title={title} size={size} accessibilityLabel={accessibilityLabel} />
            );
        default:
            return (
                <AILogoColor title={title} size={size} accessibilityLabel={accessibilityLabel} />
            );
    }
}

function AILogoFilled({ title, size = 'md', accessibilityLabel }: AILogoProps) {
    const theme = useTheme();
    const path = (
        <>
            <Path
                fill={theme.colors.foregroundStrong}
                d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47718 22 2 17.5228 2 12C2 6.47717 6.47717 2.00003 12 2ZM12.457 5.83594C12.3171 5.38848 11.6829 5.38848 11.543 5.83594L10.5586 8.98047L8.44141 7.87109C8.07522 7.67946 7.67946 8.07522 7.87109 8.44141L8.98047 10.5586L5.83594 11.543C5.38848 11.6829 5.38848 12.3171 5.83594 12.457L8.98047 13.4404L7.87109 15.5586C7.67946 15.9248 8.07522 16.3205 8.44141 16.1289L10.5586 15.0186L11.543 18.1641C11.6829 18.6115 12.3171 18.6115 12.457 18.1641L13.4404 15.0186L15.5586 16.1289C15.9248 16.3205 16.3205 15.9248 16.1289 15.5586L15.0186 13.4404L18.1641 12.457C18.6115 12.3171 18.6115 11.6829 18.1641 11.543L15.0186 10.5586L16.1289 8.44141C16.3205 8.07522 15.9248 7.67946 15.5586 7.87109L13.4404 8.98047L12.457 5.83594Z"
            />
        </>
    );

    return (
        <Svg
            accessible={!!accessibilityLabel || !!title}
            title={title}
            accessibilityLabel={accessibilityLabel}
            role="img"
            testID="ai-logo"
            viewBox={'0 0 24 24'}
            height={getSvgSize(size)}
            width={getSvgSize(size)}
            fill="none"
        >
            {path}
        </Svg>
    );
}

function AILogoStroke({ title, size = 'md', accessibilityLabel }: AILogoProps) {
    const theme = useTheme();
    const path = (
        <>
            <Path
                fill={theme.colors.foregroundStrong}
                d="M11.543 5.83594C11.6829 5.38848 12.3171 5.38848 12.457 5.83594L13.4404 8.98047L15.5586 7.87109C15.9248 7.67947 16.3205 8.07522 16.1289 8.44141L15.0186 10.5586L18.1641 11.543C18.6115 11.6829 18.6115 12.3171 18.1641 12.457L15.0186 13.4404L16.1289 15.5586C16.3205 15.9248 15.9248 16.3205 15.5586 16.1289L13.4404 15.0186L12.457 18.1641C12.3171 18.6115 11.6829 18.6115 11.543 18.1641L10.5586 15.0186L8.44141 16.1289C8.07522 16.3205 7.67946 15.9248 7.87109 15.5586L8.98047 13.4404L5.83594 12.457C5.38848 12.3171 5.38848 11.6829 5.83594 11.543L8.98047 10.5586L7.87109 8.44141C7.67946 8.07522 8.07522 7.67946 8.44141 7.87109L10.5586 8.98047L11.543 5.83594Z"
            />
            <Path
                fill-rule="evenodd"
                clip-rule="evenodd"
                fill={theme.colors.foregroundStrong}
                d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4.2002C7.69218 4.2002 4.2002 7.69218 4.2002 12C4.2002 16.3078 7.69218 19.7998 12 19.7998C16.3078 19.7998 19.7998 16.3078 19.7998 12C19.7998 7.69218 16.3078 4.2002 12 4.2002Z"
            />
        </>
    );

    return (
        <Svg
            accessible={!!accessibilityLabel || !!title}
            title={title}
            accessibilityLabel={accessibilityLabel}
            role="img"
            testID="ai-logo"
            viewBox={'0 0 24 24'}
            height={getSvgSize(size)}
            width={getSvgSize(size)}
            fill="none"
        >
            {path}
        </Svg>
    );
}

function AILogoColor({ title, size = 'md', accessibilityLabel }: AILogoProps) {
    const gradientId = `AILogo-gradient-${useId()}`;
    const theme = useTheme();
    const path = (
        <>
            <Path
                d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
                fill={`url(#${gradientId})`}
            />
            <Path
                fill={theme.colors.primary400}
                d="m14.981 13.034 1.465 2.798a.455.455 0 0 1-.614.614l-2.798-1.465a2.23 2.23 0 0 0-2.068 0l-2.798 1.465a.455.455 0 0 1-.614-.614l1.465-2.798a2.23 2.23 0 0 0 0-2.068L7.554 8.168a.455.455 0 0 1 .614-.614l2.798 1.465a2.23 2.23 0 0 0 2.068 0l2.798-1.465c.395-.207.82.22.614.614l-1.465 2.798a2.23 2.23 0 0 0 0 2.068Z"
            />
            <Path
                fill={theme.colors.neutral0}
                d="m13.561 15.22-1.069 3.419a.516.516 0 0 1-.984 0l-1.07-3.42a2.528 2.528 0 0 0-1.658-1.658l-3.419-1.069a.516.516 0 0 1 0-.984l3.42-1.07a2.528 2.528 0 0 0 1.658-1.658l1.069-3.419a.516.516 0 0 1 .984 0l1.07 3.42c.247.79.866 1.41 1.658 1.658l3.419 1.069a.516.516 0 0 1 0 .984l-3.42 1.07a2.528 2.528 0 0 0-1.658 1.658Z"
            />
        </>
    );

    const defs = (
        <LinearGradient
            id={gradientId}
            x1={'2'}
            x2={'2'}
            y1={'2'}
            y2={'22'}
            gradientUnits="userSpaceOnUse"
        >
            <Stop offset="0.25" stopColor={theme.colors.utilityAiStrongStart} />
            <Stop offset="0.75" stopColor={theme.colors.utilityAiStrongEnd} />
        </LinearGradient>
    );

    return (
        <Svg
            accessible={!!accessibilityLabel || !!title}
            title={title}
            accessibilityLabel={accessibilityLabel}
            role="img"
            testID="ai-logo"
            viewBox={'0 0 24 24'}
            height={getSvgSize(size)}
            width={getSvgSize(size)}
            fill="none"
        >
            {path}
            {defs}
        </Svg>
    );
}

AILogo.displayName = 'AILogo';
